import { useEffect, useRef, useCallback } from 'react'

/**
 * Industry-standard auto-refresh hook.
 *
 * Strategy (matches what apps like Linear, Notion, and GitHub use):
 *  1. **Visibility refresh** – when the browser tab becomes visible again after
 *     being hidden, refresh immediately (catches stale data after switching tabs).
 *  2. **Focus refresh** – when the window regains focus (e.g. user switches back
 *     from another app), refresh if the data is older than `staleTtlMs`.
 *  3. **Interval refresh** – background polling every `intervalMs` while the tab
 *     is visible, so long-running sessions stay fresh.
 *
 * All three are opt-in via the options object.
 *
 * @param refresh   Callback that fetches fresh data. Must be stable (useCallback).
 * @param options
 *   - intervalMs      Background poll interval (ms). Default 5 minutes. Pass 0 to disable.
 *   - staleTtlMs      How old data must be (ms) before a focus/visibility event triggers
 *                     a refresh. Default 60 seconds.
 *   - enabled         Master switch. Set to false to pause all refreshes (e.g. while
 *                     the user is editing something). Default true.
 */
export function useAutoRefresh(
  refresh: () => void,
  {
    intervalMs = 5 * 60 * 1000,
    staleTtlMs = 60 * 1000,
    enabled = true,
  }: {
    intervalMs?: number
    staleTtlMs?: number
    enabled?: boolean
  } = {}
) {
  const lastFetchedAt = useRef<number>(Date.now())
  const stableRefresh = useRef(refresh)
  stableRefresh.current = refresh

  // Track last fetch time so stale checks are accurate
  const doRefresh = useCallback(() => {
    lastFetchedAt.current = Date.now()
    stableRefresh.current()
  }, [])

  // 1. Background interval while tab is visible
  useEffect(() => {
    if (!enabled || intervalMs <= 0) return
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        doRefresh()
      }
    }, intervalMs)
    return () => clearInterval(id)
  }, [enabled, intervalMs, doRefresh])

  // 2. Visibility change — refresh immediately when tab becomes visible
  useEffect(() => {
    if (!enabled) return
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const age = Date.now() - lastFetchedAt.current
        if (age >= staleTtlMs) {
          doRefresh()
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [enabled, staleTtlMs, doRefresh])

  // 3. Window focus — refresh if data is stale
  useEffect(() => {
    if (!enabled) return
    const onFocus = () => {
      const age = Date.now() - lastFetchedAt.current
      if (age >= staleTtlMs) {
        doRefresh()
      }
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [enabled, staleTtlMs, doRefresh])
}
